import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Lock, ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/brand/BrandLogo";
import Seo from "@/components/Seo";

export default function AdminLogin() {
  const { login, user, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid email or password.";
      toast.error(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login"
      className="min-h-screen bg-[#F5EFE7] text-[#451C25] flex items-center justify-center px-6"
    >
      <Seo title="Admin | Destino Europe" description="Destino Europe admin." path="/admin" noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-12" data-testid="admin-login-brand">
          <BrandLogo variant="endorsement" layout="stacked" size="sm" color="#451C25" markVariant="burgundy" subColor="#897756" />
        </Link>

        <div className="bg-white border border-[#451C25]/15 p-10">
          <div className="flex items-center justify-center w-12 h-12 border border-[#451C25]/20 mb-6">
            <Lock size={18} className="text-[#451C25]" />
          </div>
          <h1 className="font-display text-3xl tracking-tight mb-2">
            Admin sign in
          </h1>
          <p className="text-[#6B4954] text-sm mb-8">
            Private console for the Destino Europe team.
          </p>

          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
                Email
              </label>
              <input
                data-testid="admin-email-input"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full bg-transparent border-b border-[#451C25]/20 py-3 focus:outline-none focus:border-[#451C25] transition-colors"
                placeholder="admin@destinoeurope.com"
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
                Password
              </label>
              <input
                data-testid="admin-password-input"
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full bg-transparent border-b border-[#451C25]/20 py-3 focus:outline-none focus:border-[#451C25] transition-colors"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#451C25] disabled:opacity-60 text-white px-6 py-4 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
            >
              {submitting ? "Signing in..." : "Sign in"}
              <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
